/**
 * i18next initialisation for To To-Do v2.
 *
 * Pattern: English-text-as-key (keys ARE English), so `en.ts` is minimal.
 * Language detection: localStorage cache first (prevents flash), then
 * navigator language. The Dexie "#language" setting is synced separately
 * by a React hook (useLanguageSync) that calls i18n.changeLanguage().
 *
 * Supported languages (start): en, sv
 * More will be added via the auto-translate pipeline.
 */

import i18n from "i18next"
import { initReactI18next } from "react-i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import { en } from "./locales/en"
import { sv } from "./locales/sv"

// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------

const resources = {
  en: { translation: en },
  sv: { translation: sv },
}

// ---------------------------------------------------------------------------
// Language menu items — used by LanguageSelect component
// ---------------------------------------------------------------------------

export const SUPPORTED_LANGUAGES = ["en", "sv"] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

// ---------------------------------------------------------------------------
// localStorage key used for flicker-free language on startup.
// Written by useLanguageSync hook whenever the Dexie "#language" changes.
// Read synchronously by LanguageDetector before React renders.
// ---------------------------------------------------------------------------
export const LANGUAGE_CACHE_KEY = "totodo:language"

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: [...SUPPORTED_LANGUAGES],

    interpolation: {
      escapeValue: false, // React already escapes
    },

    detection: {
      // 1) localStorage cache (written by useLanguageSync)
      // 2) navigator (browser language)
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LANGUAGE_CACHE_KEY,
      caches: ["localStorage"],
      lookupCookie: undefined, // don't use cookies
    },

    // Don't load missing keys from server
    saveMissing: false,

    // Return key itself when no translation found (English-as-key pattern)
    returnNull: false,
    returnEmptyString: false,
  })

export default i18n
